import { createSlice } from "@reduxjs/toolkit";

import {
  createEmailTemplate,
  deleteEmailTemplate,
  fetchEmailTemplates,
  updateEmailTemplate,
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "./thunks";

import type {
  ActiveCompany,
  EmailTemplate,
  EmailTemplateCategory,
  EmailTemplatesState,
} from "./types";

const initialState: EmailTemplatesState = {
  items: [],
  loading: false,
  error: null,
  saving: false,
  activeCompany: null,
  categories: [],
  categoriesLoading: false,
};

const emailTemplatesSlice = createSlice({
  name: "emailTemplates",
  initialState,
  reducers: {
    clearEmailTemplatesError(state) {
      state.error = null;
    },
    setActiveCompany(state, action: { payload: ActiveCompany | null }) {
      state.activeCompany = action.payload;
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmailTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.items = (action.payload as EmailTemplate[]) || [];
      })
      .addCase(fetchEmailTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load templates";
      })
      .addCase(createEmailTemplate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createEmailTemplate.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createEmailTemplate.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to create template";
      })
      .addCase(updateEmailTemplate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateEmailTemplate.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateEmailTemplate.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to update template";
      })
      .addCase(deleteEmailTemplate.fulfilled, (state, action) => {
        const removedId = action.payload as number;
        state.items = state.items.filter((t) => t.id !== removedId);
      })
      .addCase(deleteEmailTemplate.rejected, (state, action) => {
        state.error = (action.payload as string) || "Failed to delete template";
      })
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories =
          (action.payload as EmailTemplateCategory[]) || [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.error =
          (action.payload as string) || "Failed to load categories";
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.categories = [
            ...state.categories,
            action.payload as EmailTemplateCategory,
          ];
        }
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        const updated = action.payload as EmailTemplateCategory;
        if (updated) {
          state.categories = state.categories.map((c) =>
            c.id === updated.id ? updated : c,
          );
        }
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        const removedId = action.payload as number;
        state.categories = state.categories.filter((c) => c.id !== removedId);
      });
  },
});

export const { clearEmailTemplatesError, setActiveCompany } =
  emailTemplatesSlice.actions;
export default emailTemplatesSlice.reducer;
